import { productImages } from './imagePaths';

export interface Product {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  specifications: {
    label: string;
    value: string;
  }[];
  images: {
    src: string;
    alt: string;
    caption?: string;
  }[];
  videos?: {
    src: string;
    title: string;
    description: string;
  }[];
  instructions: {
    step: number;
    title: string;
    description: string;
    tips?: string;
  }[];
  relatedProducts: string[];
  price?: string;
  availability: string;
  stripeCheckoutUrl?: string;
}

export const products: Product[] = [
  {
    id: 'packaging-coverage',
    title: 'Flexcrete Waterproofing',
    subtitle: 'Flexcrete is available in three convenient sizes for all project needs',
    description: 'Flexcrete is a 2-component, waterproof cementitious mortar. The liquid component is a blend of polymers while the dry component is a proprietary blend of cement and other admixtures. When mixed and applied, one achieves a waterproof membrane with incredible adhesion. Flexcrete is extremely resistant to all corrosive agents, exhibits concrete-like characteristics as well as being flexible enough to absorb the formation of cracks in the substrate and underlying movements in the structure itself.',
    features: [
      '½ Gallon Jug - Perfect for small repairs',
      '1 Gallon Jug - Ideal for medium-sized projects',
      '5 Gallon Pail - Best for large applications',
      'Maximum Adhesion - Bonds to all substrates',
      'Designed for submerged applications',
      'Works on positive and negative water pressure',
      'Impervious to UV rays',
      'Resists all corrosive agents',
      'Immune to freeze-thaw cycles',
      'Protects against efflorescence',
      'Adheres to CMU blocks, concrete, tile, metal, polystyrene',
      'Can be used as final finish in pools and water structures'
    ],
    specifications: [
      { label: 'Package Sizes', value: '½ Gal, 1 Gal, 5 Gal' },
      { label: 'Pot Life', value: '20-25 minutes after mixing' },
      { label: 'Application Temperature', value: '5°C - 35°C (40°F - 100°F)' },
      { label: 'Coverage - Concrete', value: '5kg/M2 on new or old concrete' },
      { label: 'Coverage - Tile', value: '6-8kg/M2 depending on joint depth' },
      { label: 'Coverage - Polystyrene', value: '7.5kg/M2 on polystyrene blocks' },
      { label: 'Coverage - CMU Block', value: '8kg/M2 on CMU block' },
      { label: 'Consistency', value: 'Thick milkshake or pancake batter' },
      { label: 'Cure Time', value: '24 hours between coats' },
      { label: 'Mixing Ratio', value: '1 measure liquid : 2 measures Type M Mortar' },
      { label: 'False Set Time', value: '90-120 seconds' },
      { label: 'Remix Time', value: '60-90 seconds' },
      { label: 'Shelf Life', value: '12 months for both components' },
      { label: 'VOC Content', value: 'Contains no VOCs' }
    ],
    images: [
      {
        src: productImages.coverageGuide,
        alt: 'Coverage calculation chart',
        caption: 'Easy-to-use coverage calculation guide'
      },
      {
        src: productImages.applicationPhoto,
        alt: 'Flexcrete application',
        caption: 'Professional waterproofing application'
      },
      {
        src: productImages.compatibilityChart,
        alt: 'Substrate compatibility chart',
        caption: 'Flexcrete compatibility with various substrates'
      }
    ],
    videos: [
      {
        src: '/videos/packaging-guide.mp4',
        title: 'Flexcrete Packaging Guide',
        description: 'Learn about the different sizes and choose the right one for your project'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Choose Your Package Size',
        description: 'Select the appropriate size based on your project scope: ½ Gal for small repairs, 1 Gal for medium projects, or 5 Gal for large applications.',
        tips: 'Calculate your total coverage area and add 10% for waste'
      },
      {
        step: 2,
        title: 'Check Application Conditions',
        description: 'Ensure ambient temperature is between 5°C - 35°C (40°F - 100°F). Check weather - no rain or frost for 24 hours after application.',
        tips: 'Avoid application in direct sunlight or extreme temperatures'
      },
      {
        step: 3,
        title: 'Prepare Storage',
        description: 'Store unopened containers in a cool, dry place away from direct sunlight. Keep containers sealed when not in use.',
        tips: 'Use oldest inventory first (FIFO method)'
      },
      {
        step: 4,
        title: 'Calculate Coverage',
        description: 'Determine how much product you need based on surface area and number of coats required (typically 2 coats recommended).',
        tips: 'Order slightly more than calculated to ensure project completion'
      }
    ],
    relatedProducts: ['mixing-preparing', 'applying', 'flashing'],
    price: 'Buy now',
    availability: 'In Stock'
  },
  {
    id: 'mixing-preparing',
    title: 'Mixing & Preparing',
    subtitle: 'Professional mixing techniques for optimal Flexcrete performance',
    description: 'Flexcrete is a specially designed 2-component mix of Acrylic Polymers designed to enhance the performance of cement. When mixed with any Type M Mortar mix it will when cured create a cementitious waterproof substrate. Additionally, the cured material will have enhanced adhesion to its substrate and once fully cured will exceed the strength of the original concrete.',
    features: [
      'Add 1 measure of Flexcrete liquid to bucket first',
      'Add 2 measures of Type M Mortar mix',
      'Mix thoroughly for 2-3 minutes with paddle mixer until homogenous',
      'Add remaining powder gradually until mix is clearly liquid but thicker than water',
      'Should have consistency of thick milkshake or pancake batter',
      'Let false set for 90-120 seconds, remix for 60-90 seconds',
      'Apply immediately - pot life: 20-25 minutes before stiffening',
      'Can extend pot life by adding shot glass of additional liquid and remixing',
      'Can mix remnants of previous batch into new batch',
      'NEVER add water to the mix'
    ],
    specifications: [
      { label: 'Mixing Ratio', value: '1 measure liquid : 2 measures Type M Mortar' },
      { label: 'Mixing Time', value: '2-3 minutes until homogenous, no clumps' },
      { label: 'False Set Time', value: '90-120 seconds' },
      { label: 'Remix Time', value: '60-90 seconds' },
      { label: 'Pot Life', value: '20-25 minutes before mix stiffens' },
      { label: 'Mixing Tool', value: 'Paddle mixer required' },
      { label: 'Consistency', value: 'Thick milkshake or pancake batter' },
      { label: 'Type M Mortar', value: 'Available in 80lb bags at material supply stores' },
      { label: 'Coverage per 80lb bag', value: '75-100 sq ft at 1/4" thickness with 2 gal liquid' },
      { label: 'Liquid Requirement', value: '1.5-2 gallons per 80lb bag depending on application' }
    ],
    images: [
      {
        src: productImages.mixingSetup,
        alt: 'Flexcrete mixing station setup',
        caption: 'Proper mixing station setup for consistent results'
      },
      {
        src: productImages.mixingDemonstration,
        alt: 'Mixing Flexcrete',
        caption: 'Achieving the perfect consistency'
      },
      {
        src: productImages.consistencyGuide,
        alt: 'Testing material consistency',
        caption: 'Should have consistency of thick milkshake'
      }
    ],
    videos: [
      {
        src: '/videos/mixing-techniques.mp4',
        title: 'Flexcrete Mixing Techniques',
        description: 'Step-by-step guide to mixing Flexcrete for optimal results'
      },
      {
        src: '/videos/preparation-guide.mp4',
        title: 'Substrate Preparation Guide',
        description: 'Complete guide to preparing surfaces for Flexcrete application'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Add Liquid Component First',
        description: 'Pour Flexcrete liquid component into a clean bucket first. This is critical for proper mixing.',
        tips: 'Always add liquid before powder - never the reverse'
      },
      {
        step: 2,
        title: 'Add Type M Mortar Mix',
        description: 'Add 2 measures of Type M Mortar mix to the liquid. Add remaining powder gradually.',
        tips: 'Use proper measuring tools for accurate ratios'
      },
      {
        step: 3,
        title: 'Mix Thoroughly',
        description: 'Mix for 2-3 minutes with a paddle mixer until homogenous with no clumps. Achieve thick milkshake consistency.',
        tips: 'Use a powerful drill with paddle attachment for best results'
      },
      {
        step: 4,
        title: 'False Set and Remix',
        description: 'Let mixture sit for 90-120 seconds (false set), then remix for 60-90 seconds.',
        tips: 'This step activates the polymer blend for maximum adhesion'
      },
      {
        step: 5,
        title: 'Apply Immediately',
        description: 'Apply Flexcrete immediately after final remix. Pot life is 20-25 minutes before mixture stiffens.',
        tips: 'Can extend pot life by adding small amount of liquid and remixing'
      }
    ],
    relatedProducts: ['packaging-coverage', 'applying', 'flashing'],
    price: 'Included with product',
    availability: 'Instructions Available'
  },
  {
    id: 'applying',
    title: 'Application Methods',
    subtitle: 'Multiple application methods for professional Flexcrete waterproofing results',
    description: 'Flexcrete can be applied using various methods depending on your project needs and preferences. Choose from paint roller, brush, trowel, squeegee, or hopper gun application. All substrates should be clean, free of dust and grease, and properly prepared per ASTM/NF DTU guidelines. Flexcrete can be used for all waterproofing jobs, both interior or exterior applications.',
    features: [
      'Paint roller application for large flat surfaces',
      'Brush application for detailed work',
      'Trowel application for thick coats',
      'Squeegee application for smooth finishes',
      'Hopper gun for spray applications (contact us for guidance)',
      '24 hours cure time between coats',
      'Only apply in ambient temps of 5-35°C (40-100°F)',
      'Do not apply in rain or under excessive temperatures',
      'No rain for first 24 hours after application',
      'No frost for first 24 hours after application',
      'If substrate is porous, humidify to saturation before application',
      'First coat should not mark with fingernail before applying second coat'
    ],
    specifications: [
      { label: 'Application Methods', value: 'Roller, brush, trowel, squeegee, hopper gun' },
      { label: 'Temperature Range', value: '5°C - 35°C (40°F - 100°F)' },
      { label: 'Cure Time Between Coats', value: '24 hours minimum (48h if ambient <10°C)' },
      { label: 'Weather Conditions', value: 'No rain/frost for 24 hours' },
      { label: 'Substrate Prep', value: 'Clean per ASTM/DTU 21 guidelines' },
      { label: 'Number of Coats', value: '2-3 coats @ 1.5mm thickness' },
      { label: 'Coverage Rate', value: '5-8kg/M2 for 2-3 coats' },
      { label: 'Substrate Compatibility', value: 'Concrete, CMU blocks, tile, metal, polystyrene, PVC' },
      { label: 'Submerged Applications', value: 'Pools, spas, fountains, storage tanks' },
      { label: 'Exterior Applications', value: 'Decks, balconies, roofs, facades, parking garages' },
      { label: 'Interior Applications', value: 'Bathrooms, kitchens, showers, basements' }
    ],
    images: [
      {
        src: productImages.rollerMethod,
        alt: 'Roller application technique',
        caption: 'Paint roller application for large surfaces'
      },
      {
        src: productImages.trowelMethod,
        alt: 'Trowel application',
        caption: 'Trowel application for detailed waterproofing'
      },
      {
        src: productImages.surfacePreparation,
        alt: 'Surface preparation process',
        caption: 'Proper substrate preparation is essential'
      },
      {
        src: productImages.applicationPhoto,
        alt: 'Application demonstration',
        caption: 'Professional application techniques'
      }
    ],
    videos: [
      {
        src: '/videos/application-guide.mp4',
        title: 'Complete Flexcrete Application Guide',
        description: 'Professional application techniques from start to finish'
      },
      {
        src: '/videos/troubleshooting.mp4',
        title: 'Application Troubleshooting',
        description: 'Common application issues and solutions'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Prepare the Substrate',
        description: 'Remove all dust, loose materials, and grease stains. Substrate should be clean per ASTM/NF DTU 21 guidelines.',
        tips: 'If substrate is porous, humidify to saturation before application'
      },
      {
        step: 2,
        title: 'Choose Application Method',
        description: 'Select roller, brush, trowel, squeegee, or hopper gun based on project requirements and surface type.',
        tips: 'Roller works best for large flat areas; trowel for detailed work'
      },
      {
        step: 3,
        title: 'Apply First Coat',
        description: 'Apply Flexcrete evenly using your chosen method. Work in sections to maintain wet edges.',
        tips: 'Ensure complete coverage with no thin spots or gaps'
      },
      {
        step: 4,
        title: 'Allow Proper Cure Time',
        description: 'Wait 24 hours before applying second coat. First coat should not mark with fingernail when ready.',
        tips: 'Protect from rain and frost during first 24 hours'
      },
      {
        step: 5,
        title: 'Apply Second Coat',
        description: 'Apply second coat perpendicular to first coat for maximum coverage and waterproofing.',
        tips: 'Two coats provide optimal waterproofing performance'
      },
      {
        step: 6,
        title: 'Final Inspection',
        description: 'Inspect completed application for any thin spots or missed areas. Touch up as needed.',
        tips: 'Allow full cure before exposing to water or applying finish materials'
      }
    ],
    relatedProducts: ['packaging-coverage', 'mixing-preparing', 'flashing'],
    price: 'Included with product',
    availability: 'Instructions Available'
  },
  {
    id: 'flashing',
    title: 'Waterproof Flashing',
    subtitle: 'Critical waterproofing at joints, seams, and transitions',
    description: `Flashing is your primary defense against water intrusion at critical areas. Flexcrete excels at flashing applications where different materials or planes meet - preventing moisture intrusion that can cause wood rot, mildew, and structural damage.

**Definition:** Thin, impermeable material applied to joints, seams, and transitions on building exteriors to block water intrusion.

**Purpose:**
- **Waterproofing:** Primary role is to prevent water infiltration at joints where different Materials or Planes meet. Examples: Roof v Wall, Deck v House, Balcony v Wall
- **Moisture Diversion:** Acts as a shield to guide water away from structural elements.
- **Structural Protection:** By keeping water out, flashing prevents common problems like wood rot and mildew growth which can compromise the buildings' structural integrity.

When it comes to Waterproofing, Flashing is an incredibly important notion to keep in mind. Flashing is the primary defense against water and moisture intrusion at the main areas of concern for waterproofing your structure. There are two key areas where waterproofing should always be considered:

1. **Changes in plane.** Where wall meets floor & where wall meets deck or balcony. Roofs often have two or more levels and flashing is needed at the changes of plane.
2. **Joints where different materials meet.** Any time you have two different materials meet you have a seam that is never waterproof. Examples include cinderblocks on foundations, tiles on decks, drains in decks, chimneys & vents on roofs, etc.

There are countless variations of two materials joined together in construction and these areas must all be considered for implications of water or moisture intrusion.`,
    features: [
      'Waterproofing at changes of plane (wall-to-floor, wall-to-deck)',
      'Sealing joints where different materials meet',
      'Prevents water intrusion at critical transitions',
      'Protects against wood rot and mildew growth',
      'Diverts moisture away from structural elements',
      'Essential for long-term structural protection'
    ],
    specifications: [
      { label: 'Purpose', value: 'Block water intrusion at joints and transitions' },
      { label: 'Primary Applications', value: 'Changes in plane, material transitions' },
      { label: 'Key Areas', value: 'Roof-wall, deck-wall, balcony-wall joints' },
      { label: 'Protection Level', value: 'Maximum waterproofing barrier' },
      { label: 'Adhesion', value: 'Bonds to all common substrates' },
      { label: 'Flexibility', value: 'Absorbs structural movements' }
    ],
    images: [
      {
        src: productImages.flashingPreparation,
        alt: 'Flashing preparation',
        caption: 'Proper preparation is critical for flashing adhesion'
      },
      {
        src: productImages.flashingApplication,
        alt: 'Flashing application at wall-deck joint',
        caption: 'Critical flashing at wall-to-deck transition'
      },
      {
        src: productImages.beforeFlashing,
        alt: 'Before flashing application',
        caption: 'Area before flashing application'
      },
      {
        src: productImages.afterFlashing,
        alt: 'After flashing application',
        caption: 'Completed flashing application'
      },
      {
        src: productImages.roofFlashing,
        alt: 'Roof flashing detail',
        caption: 'Proper flashing at roof level changes'
      },
      {
        src: productImages.balconyWaterproofing,
        alt: 'Balcony waterproofing',
        caption: 'Complete balcony waterproofing with flashing'
      }
    ],
    videos: [
      {
        src: '/videos/flashing-guide.mp4',
        title: 'Waterproof Flashing Techniques',
        description: 'How to properly flash critical waterproofing areas'
      },
      {
        src: '/videos/cold-joint-flashing.mp4',
        title: 'Cold Joint Flashing',
        description: 'Flashing at wall-balcony and wall-deck cold joints'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Identify Critical Areas',
        description: 'Locate all changes in plane (wall-floor, wall-deck) and material transitions (block-concrete, tile-concrete, etc.).',
        tips: 'These areas are primary entry points for water intrusion'
      },
      {
        step: 2,
        title: 'Clean and Prepare Joints',
        description: 'Remove all debris, dust, and loose materials from joints. Ensure surfaces are clean per ASTM guidelines.',
        tips: 'Proper preparation is critical for flashing adhesion'
      },
      {
        step: 3,
        title: 'Apply Flexcrete to Transition',
        description: 'Apply Flexcrete generously at the joint, covering both surfaces that meet. Build up thickness at critical areas.',
        tips: 'Extend flashing 4-6 inches onto each surface for best protection'
      },
      {
        step: 4,
        title: 'Create Moisture Barrier',
        description: 'Ensure continuous coverage with no gaps or thin spots. Flexcrete should form complete barrier against water intrusion.',
        tips: 'Pay special attention to corners and complex transitions'
      },
      {
        step: 5,
        title: 'Allow Proper Cure',
        description: 'Let flashing cure for 24 hours before applying additional coats or finish materials.',
        tips: 'Protect from rain and frost during cure period'
      }
    ],
    relatedProducts: ['packaging-coverage', 'mixing-preparing', 'applying', 'flashing-kit'],
    price: 'Same as Flexcrete product',
    availability: 'Technique Guide Available'
  },
  {
    id: 'flashing-kit',
    title: 'Kit',
    subtitle: 'Complete kit for flashing applications and crack repair',
    description: 'The Flashing Kit or CRK (Crack Repair Kit) contains ½ Gallon of Polymer, 15 lbs of compound as well as 10 linear feet of mesh. With this kit you can do flashing applications (cold joins, transitions of plane, etc.) or repair cracks in concrete slabs. The kit provides enough material to "chase" linear feet of cracks or 10 feet of floor wall transition.',
    features: [
      '½ Gallon of Polymer included',
      '15 lbs of compound included',
      '10 linear feet of mesh included',
      'Perfect for flashing applications',
      'Ideal for crack repair in concrete slabs',
      'Covers 10 linear feet of floor-wall transition',
      'All-in-one solution for critical waterproofing',
      'Professional-grade materials',
      'Easy to use with included instructions'
    ],
    specifications: [
      { label: 'Polymer Quantity', value: '½ Gallon' },
      { label: 'Compound Quantity', value: '15 lbs' },
      { label: 'Mesh Length', value: '10 linear feet' },
      { label: 'Coverage - Flashing', value: '10 feet of floor-wall transition' },
      { label: 'Coverage - Crack Repair', value: '10 linear feet of cracks' },
      { label: 'Application Type', value: 'Flashing & Crack Repair' },
      { label: 'Cure Time', value: 'Overnight (before further treatment)' },
      { label: 'Substrate Prep', value: 'SSD (Saturated Surface Dry)' }
    ],
    images: [
      {
        src: productImages.flashingPreparation,
        alt: 'Flashing kit preparation',
        caption: 'Kit includes all materials needed for flashing applications'
      },
      {
        src: productImages.flashingApplication,
        alt: 'Flashing application with kit',
        caption: 'Professional flashing application using kit materials'
      },
      {
        src: productImages.beforeFlashing,
        alt: 'Before flashing application',
        caption: 'Area before flashing application'
      },
      {
        src: productImages.afterFlashing,
        alt: 'After flashing application',
        caption: 'Completed flashing application'
      }
    ],
    videos: [
      {
        src: '/videos/flashing-kit-guide.mp4',
        title: 'Flashing Kit Application Guide',
        description: 'Complete guide to using the Flashing Kit for flashing applications'
      },
      {
        src: '/videos/crack-repair-kit.mp4',
        title: 'Crack Repair Kit Guide',
        description: 'Step-by-step guide to using the kit for crack repair'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Flashing Application - Clean Area',
        description: 'Clean area to be treated taking care to remove old layers of paint or material. To the extent possible it is always best to get back to the original substrate.',
        tips: 'Proper surface preparation ensures maximum adhesion'
      },
      {
        step: 2,
        title: 'Flashing Application - Dampen Surface',
        description: 'Dampen areas to be treated with water. Concrete should be wet, but there should be no standing water. The industry term is SSD, Saturated Surface Dry. The moisture should be inside the concrete and not on its surface.',
        tips: 'SSD condition is critical for proper material bonding'
      },
      {
        step: 3,
        title: 'Flashing Application - Apply First Coat',
        description: 'Center the mesh on area to be treated noting distance up and down from the join or corner being treated. Apply first coat of material extending past the mesh by an inch or two.',
        tips: 'Ensure mesh is properly centered before applying material'
      },
      {
        step: 4,
        title: 'Flashing Application - Embed Mesh',
        description: 'Immediately, while material is still wet place the mesh over the wet material and then apply a second layer of material over the first. Apply material until you no longer see the mesh. Attempt to flatten the mesh into the material so that there are no air gaps.',
        tips: 'Work quickly while material is wet to ensure proper mesh embedding'
      },
      {
        step: 5,
        title: 'Flashing Application - Cure',
        description: 'Let cure overnight before further treatment.',
        tips: 'Do not apply additional materials until fully cured'
      },
      {
        step: 6,
        title: 'Crack Repair - Grind Open Cracks',
        description: 'Grind open all visible cracks, including cold joints and other questionable areas. Grind with a grinding blade that leaves behind a square or "butt" cut.',
        tips: 'Proper crack preparation ensures material penetration'
      },
      {
        step: 7,
        title: 'Crack Repair - Clean and Dampen',
        description: 'Clean out the area ensuring all dust and loose material is removed. Dampen areas to be treated with Water. SSD (Saturated Surface Dry).',
        tips: 'Complete cleaning is essential for proper adhesion'
      },
      {
        step: 8,
        title: 'Crack Repair - Fill Crack',
        description: 'Apply material into the cleaned crack, ensuring the material is pushed all the way into the crack and overlap crack 3 to 4 inches on each side of the crack. Ensure a good thick layer is applied.',
        tips: 'Overlap ensures complete waterproofing coverage'
      },
      {
        step: 9,
        title: 'Crack Repair - Apply Mesh',
        description: 'Center the mesh over the crack. Do not push too hard as the mesh should be caught between two layers of material and not pushed to the concrete so that all the material is on top of the mesh.',
        tips: 'Mesh should be embedded, not pushed to the substrate'
      },
      {
        step: 10,
        title: 'Crack Repair - Second Coat',
        description: 'Apply second coat on top of the newly placed mesh until the mesh is no longer visible.',
        tips: 'Complete coverage ensures waterproof barrier'
      },
      {
        step: 11,
        title: 'Crack Repair - Cure',
        description: 'Let cure overnight before going over with other materials.',
        tips: 'Allow full cure for maximum strength'
      }
    ],
    relatedProducts: ['flashing', 'mixing-preparing', 'applying'],
    price: '$58.50',
    availability: 'In Stock',
    stripeCheckoutUrl: 'https://buy.stripe.com/5kQ4gzgKndOQc9zgJC9oc00'
  },
  {
    id: 'technical-specifications',
    title: 'Technical Specifications & Chemical Resistance',
    subtitle: 'Comprehensive technical data and chemical resistance properties of Flexcrete',
    description: 'Flexcrete exhibits exceptional adhesive properties and chemical resistance. The product has been tested for adhesion to various substrates and resistance to corrosive agents. All pull tests were performed with DEFELSKO Model POSITEST ATA-B, with certificate No. 21-692074 from September 23, 2021.',
    features: [
      'Exceptional adhesion to all construction substrates',
      'Resistance to chloride acid @ 10%',
      'Resistance to sulfuric acid @ 10%',
      'Resistance to liquid chlorine @ 15%',
      'Resistance to salt 100g/Lt',
      'Resistance to hydrogen peroxide @ 35g/Lt',
      'Resistance to chlorine tablet 100g',
      'No reaction after 24-48 hours of contact/submersion',
      'Exceeds strength of original concrete when fully cured',
      'Built-in high degree of elasticity for structural movement'
    ],
    specifications: [
      { label: 'CMU Block Adhesion', value: '2.79 MPa (405 psi)' },
      { label: 'Polystyrene Block Adhesion', value: '0.75 MPa (110 psi)' },
      { label: 'Concrete Slab Adhesion', value: '2.46 MPa (357 psi)' },
      { label: 'Tile Adhesion', value: '2.35 MPa (341 psi)' },
      { label: 'PVC Adhesion', value: '0.40 MPa (59 psi)' },
      { label: 'Steel Adhesion', value: '1.92 MPa (279 psi)' },
      { label: 'Chloride Acid Resistance', value: '10% concentration - no reaction' },
      { label: 'Sulfuric Acid Resistance', value: '10% concentration - no reaction' },
      { label: 'Liquid Chlorine Resistance', value: '15% concentration - no reaction' },
      { label: 'Salt Resistance', value: '100g/Lt - no reaction' },
      { label: 'Hydrogen Peroxide Resistance', value: '35g/Lt - no reaction' },
      { label: 'Chlorine Tablet Resistance', value: '100g - no reaction' },
      { label: 'Test Duration', value: '24-48 hours contact/submersion' },
      { label: 'Elasticity', value: 'High degree - absorbs structural movements' },
      { label: 'Final Strength', value: 'Exceeds original concrete strength' }
    ],
    images: [
      {
        src: productImages.chemicalTesting,
        alt: 'Chemical resistance testing',
        caption: 'Flexcrete tested against various corrosive agents'
      },
      {
        src: productImages.adhesionTesting,
        alt: 'Adhesion testing equipment',
        caption: 'DEFELSKO Model POSITEST ATA-B testing equipment'
      },
      {
        src: productImages.compatibilityChart,
        alt: 'Substrate compatibility chart',
        caption: 'Flexcrete adhesion to various construction substrates'
      }
    ],
    videos: [
      {
        src: '/videos/technical-testing.mp4',
        title: 'Flexcrete Technical Testing',
        description: 'Comprehensive testing of adhesion and chemical resistance properties'
      }
    ],
    instructions: [
      {
        step: 1,
        title: 'Substrate Preparation',
        description: 'Ensure substrate is clean per ASTM guidelines. Remove all dust, loose ends, and grease stains.',
        tips: 'Proper substrate preparation is critical for maximum adhesion'
      },
      {
        step: 2,
        title: 'Temperature Requirements',
        description: 'Apply only in ambient temperatures of 5-35°C (40-100°F). Do not apply in rain or excessive temperatures.',
        tips: 'Check weather conditions - no rain or frost for first 24 hours'
      },
      {
        step: 3,
        title: 'Porous Substrate Treatment',
        description: 'If substrate is porous, humidify to saturation before application of Flexcrete.',
        tips: 'This ensures proper bonding and prevents premature drying'
      },
      {
        step: 4,
        title: 'Application Timing',
        description: 'Wait 24 hours between each application. First coat should not mark with fingernail before applying second coat.',
        tips: 'Allow 48 hours if ambient temperature is below 10°C'
      },
      {
        step: 5,
        title: 'Clean Up',
        description: 'Clean all tools with water before the product dries on them.',
        tips: 'Immediate cleanup prevents tool damage and ensures reusability'
      }
    ],
    relatedProducts: ['packaging-coverage', 'mixing-preparing', 'applying'],
    price: 'Technical data included',
    availability: 'Specifications Available'
  }
];
